import { useState, useCallback, useEffect } from 'react';
import { CaseStudyQuestion } from '../../../types';

type StepAnswersCorrect = Record<number, number | undefined>;

export interface CaseStudyQuestionState {
  currentStepIndex: number;
  /** Estado interno de seleção (usado apenas no modo standalone, não em isTest) */
  stepAnswers: Record<number, number | undefined>;
  stepAnswersCorrect: StepAnswersCorrect;
  questionTextShow: boolean;
  questionVideoStart: boolean;
  modalVideoOption: boolean;
  LSBVideo: boolean;
  formulaModalShow: boolean;
}

interface UseCaseStudyQuestionStateProps {
  question: CaseStudyQuestion;
  isTest?: boolean;
  resolved?: boolean;
  externalStepAnswers?: Record<number, number>;
  externalStepIndex?: number;
  initialStepIndex?: number;
  onStepAnswerChange?: (stepId: number, answerId: number) => void;
}

export const useCaseStudyQuestionState = ({
  question,
  isTest = false,
  resolved = false,
  externalStepAnswers,
  externalStepIndex,
  initialStepIndex,
  onStepAnswerChange,
}: UseCaseStudyQuestionStateProps) => {
  // Inicializador lazy: semeia o passo do deep link sem render intermediário no
  // passo 1. Índice fora da faixa cai em 0.
  const [state, setState] = useState<CaseStudyQuestionState>(() => ({
    currentStepIndex:
      initialStepIndex !== undefined &&
      initialStepIndex >= 0 &&
      initialStepIndex < question.step_multiple.length
        ? initialStepIndex
        : 0,
    stepAnswers: {},
    stepAnswersCorrect: {},
    questionTextShow: false,
    questionVideoStart: false,
    modalVideoOption: false,
    LSBVideo: false,
    formulaModalShow: false,
  }));

  const totalSteps = question.step_multiple.length;
  // Em isTest=true, o step ativo é controlado externamente (LecturesFooterPanel)
  const requestedStepIndex =
    isTest && externalStepIndex !== undefined ? externalStepIndex : state.currentStepIndex;
  // Clamp: quem troca a questão selecionada é responsável por zerar o índice da
  // etapa, e um índice fora da faixa deixaria o card sem enunciado nem alternativas
  // (CaseStudyQuestion retorna null sem currentStep). Com etapas posicionadas
  // individualmente o item tem uma única etapa, então qualquer índice > 0 estoura.
  const effectiveStepIndex = Math.min(Math.max(requestedStepIndex, 0), totalSteps - 1);
  const currentStep = question.step_multiple[effectiveStepIndex];

  // Quando resolved=true, preenche stepAnswersCorrect para todos os steps
  useEffect(() => {
    if (!resolved) return;

    const correctMap: StepAnswersCorrect = {};
    question.step_multiple.forEach((step) => {
      const correct = step.answers.find((a) => a.correct === 1);
      correctMap[step.id] = correct?.answer_id;
    });
    setState((prev) => ({ ...prev, stepAnswersCorrect: correctMap }));
  }, [resolved, question.step_multiple]);

  // Retorna o answerId selecionado para o step atual
  const getSelectedAnswerId = (stepId: number): number | undefined => {
    if (isTest) return externalStepAnswers?.[stepId];
    return state.stepAnswers[stepId];
  };

  // Todos os steps têm resposta selecionada (modo standalone)
  const allStepsAnswered =
    !isTest &&
    question.step_multiple.every((step) => state.stepAnswers[step.id] !== undefined);

  // Usuário já clicou em "Salvar e ver resultado" (ao menos um step foi confirmado)
  const isAlreadySubmitted = Object.values(state.stepAnswersCorrect).some(
    (v) => v !== undefined,
  );

  const selectAnswer = useCallback(
    (answerId: number) => {
      if (!currentStep) return;

      if (isTest) {
        onStepAnswerChange?.(currentStep.id, answerId);
      } else {
        // Salva seleção sem limpar confirmações (submit é global, não por step)
        setState((prev) => ({
          ...prev,
          stepAnswers: { ...prev.stepAnswers, [currentStep.id]: answerId },
        }));
      }
    },
    [currentStep, isTest, onStepAnswerChange],
  );

  // Revela correto/errado de TODOS os steps de uma vez
  const handleSubmitAll = useCallback(() => {
    const correctMap: StepAnswersCorrect = {};
    question.step_multiple.forEach((step) => {
      const correct = step.answers.find((a) => a.correct === 1);
      correctMap[step.id] = correct?.answer_id;
    });
    setState((prev) => ({ ...prev, stepAnswersCorrect: correctMap }));
  }, [question.step_multiple]);

  const goToStep = useCallback((index: number) => {
    setState((prev) => ({ ...prev, currentStepIndex: index }));
  }, []);

  const goToPrev = useCallback(() => {
    setState((prev) => ({
      ...prev,
      currentStepIndex: Math.max(0, prev.currentStepIndex - 1),
    }));
  }, []);

  const goToNext = useCallback(() => {
    setState((prev) => ({
      ...prev,
      currentStepIndex: Math.min(totalSteps - 1, prev.currentStepIndex + 1),
    }));
  }, [totalSteps]);

  const handlePlayVideo = useCallback(() => {
    if (question?.video_embed_libras) {
      setState((prev) => ({ ...prev, LSBVideo: true, questionVideoStart: true }));
    } else {
      setState((prev) => ({ ...prev, questionVideoStart: true }));
    }
    //eslint-disable-next-line
  }, [question?.video_embed_libras]);

  const setQuestionTextShow = useCallback((show: boolean) => {
    setState((prev) => ({ ...prev, questionTextShow: show }));
  }, []);

  const setQuestionVideoStart = useCallback((show: boolean) => {
    setState((prev) => ({ ...prev, questionVideoStart: show }));
  }, []);

  const setModalVideoOption = useCallback((show: boolean) => {
    setState((prev) => ({ ...prev, modalVideoOption: show }));
  }, []);

  const setLSBVideo = useCallback((show: boolean) => {
    setState((prev) => ({ ...prev, LSBVideo: show }));
  }, []);

  const setFormulaModalShow = useCallback((show: boolean) => {
    setState((prev) => ({ ...prev, formulaModalShow: show }));
  }, []);

  return {
    state,
    currentStep,
    effectiveStepIndex,
    totalSteps,
    allStepsAnswered,
    isAlreadySubmitted,
    getSelectedAnswerId,
    selectAnswer,
    handleSubmitAll,
    goToStep,
    goToPrev,
    goToNext,
    handlePlayVideo,
    setQuestionTextShow,
    setQuestionVideoStart,
    setModalVideoOption,
    setLSBVideo,
    setFormulaModalShow,
  };
};
